import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import type { HelpArticle } from "../lib/types";
import {
  fetchArticleById,
  createArticle,
  updateArticle,
  checkSlugUnique,
  uploadImage,
} from "../lib/admin-data";
import { renderMarkdown } from "../lib/markdown";
import { SEO } from "../components/SEO";

export function AdminEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [article, setArticle] = useState<Partial<HelpArticle>>({
    title: "",
    description: "",
    content_markdown: "",
    slug: "/",
    parent_slug: null,
    nav_order: 0,
    page_type: "article",
    is_published: false,
  });
  const [originalSlug, setOriginalSlug] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [slugWarning, setSlugWarning] = useState(false);

  useEffect(() => {
    if (!isNew && id) {
      loadArticle(id);
    }
  }, [id]);

  async function loadArticle(articleId: string) {
    setLoading(true);
    try {
      const data = await fetchArticleById(articleId);
      if (!data) {
        setError("Article not found");
        return;
      }
      setArticle(data);
      setOriginalSlug(data.slug);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unknown error");
    }
    setLoading(false);
  }

  function update<K extends keyof HelpArticle>(key: K, value: HelpArticle[K]) {
    setArticle((prev) => ({ ...prev, [key]: value }));
  }

  useEffect(() => {
    if (originalSlug && article.slug && article.slug !== originalSlug) {
      setSlugWarning(true);
    } else {
      setSlugWarning(false);
    }
  }, [article.slug, originalSlug]);

  async function handleSave() {
    setError(null);
    setSaving(true);

    const slug = article.slug || "";
    if (!slug.startsWith("/")) {
      setError("Slug must begin with /");
      setSaving(false);
      return;
    }

    const isUnique = await checkSlugUnique(slug, isNew ? undefined : id);
    if (!isUnique) {
      setError("Slug already exists. Choose a unique slug.");
      setSaving(false);
      return;
    }

    try {
      const input = {
        title: article.title || "",
        description: article.description || "",
        content_markdown: article.content_markdown || "",
        parent_slug: article.parent_slug || null,
        nav_order: article.nav_order || 0,
        page_type: article.page_type || "article",
        is_published: article.is_published ?? false,
      };

      if (isNew) {
        const created = await createArticle({ ...input, slug });
        navigate(`/admin/help/edit/${created.id}`, { replace: true });
      } else if (id) {
        await updateArticle(id, input, originalSlug, slug);
        setOriginalSlug(slug);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unknown error");
    }
    setSaving(false);
  }

  function insertText(before: string, after: string = "") {
    const textarea = document.getElementById("markdown-editor") as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const selected = text.slice(start, end);
    const newText = text.slice(0, start) + before + selected + after + text.slice(end);
    update("content_markdown", newText);
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = start + before.length;
      textarea.selectionEnd = end + before.length;
    }, 0);
  }

  function insertLinePrefix(prefix: string) {
    const textarea = document.getElementById("markdown-editor") as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const text = textarea.value;
    const lineStart = text.lastIndexOf("\n", start - 1) + 1;
    const newText = text.slice(0, lineStart) + prefix + text.slice(lineStart);
    update("content_markdown", newText);
    setTimeout(() => textarea.focus(), 0);
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ["image/png", "image/jpeg", "image/webp", "image/gif", "image/svg+xml"];
    if (!allowed.includes(file.type)) {
      alert("Only PNG, JPEG, WebP, GIF, and SVG images are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be under 5 MB.");
      return;
    }

    try {
      const url = await uploadImage(file);
      const relativeUrl = url.replace(/^https?:\/\/[^/]+/, "");
      insertText(`![${file.name.replace(/\.[^.]+$/, "")}](${relativeUrl})`);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Upload failed");
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
      </div>
    );
  }

  const previewHtml = renderMarkdown(article.content_markdown || "").html;

  return (
    <>
      <SEO
        title={isNew ? "New Article — Admin" : `Edit: ${article.title} — Admin`}
        description="Help Centre article editor"
        canonical="https://help.trustreq.ca/admin/help"
      />
      <div className="admin-page">
        <div className="admin-header">
          <div className="admin-header-left">
            <Link to="/admin/help" className="btn btn-secondary">← Back</Link>
            <h1 className="admin-title">{isNew ? "New Article" : "Edit Article"}</h1>
          </div>
          <div className="admin-header-right">
            <button
              className="btn btn-secondary"
              onClick={() => setShowPreview(!showPreview)}
            >
              {showPreview ? "Edit" : "Preview"}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        {error && <div className="admin-error">{error}</div>}
        {slugWarning && (
          <div className="admin-warning">
            Warning: Changing the slug of a published article will break existing links.
            A redirect from the old slug to the new slug will be created automatically.
          </div>
        )}

        {showPreview ? (
          <div className="article-body markdown-body admin-preview" dangerouslySetInnerHTML={{ __html: previewHtml }} />
        ) : (
          <div className="editor-layout">
            <div className="editor-main">
              <div className="form-field">
                <label className="form-label" htmlFor="title">Title</label>
                <input
                  id="title"
                  className="form-input"
                  value={article.title || ""}
                  onChange={(e) => update("title", e.target.value)}
                />
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="description">Description</label>
                <input
                  id="description"
                  className="form-input"
                  value={article.description || ""}
                  onChange={(e) => update("description", e.target.value)}
                />
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label className="form-label" htmlFor="slug">Slug (must start with /)</label>
                  <input
                    id="slug"
                    className="form-input"
                    value={article.slug || ""}
                    onChange={(e) => update("slug", e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label" htmlFor="page_type">Page Type</label>
                  <select
                    id="page_type"
                    className="form-input"
                    value={article.page_type || "article"}
                    onChange={(e) => update("page_type", e.target.value as HelpArticle["page_type"])}
                  >
                    <option value="article">Article</option>
                    <option value="section">Section</option>
                    <option value="home">Home</option>
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label" htmlFor="nav_order">Nav Order</label>
                  <input
                    id="nav_order"
                    type="number"
                    className="form-input"
                    value={article.nav_order || 0}
                    onChange={(e) => update("nav_order", parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label className="form-label" htmlFor="parent_slug">Parent Slug</label>
                  <input
                    id="parent_slug"
                    className="form-input"
                    placeholder="(none for top-level)"
                    value={article.parent_slug || ""}
                    onChange={(e) => update("parent_slug", e.target.value || null)}
                  />
                </div>
                <div className="form-field form-check-field">
                  <label className="form-check">
                    <input
                      type="checkbox"
                      checked={article.is_published ?? false}
                      onChange={(e) => update("is_published", e.target.checked)}
                    />
                    Published
                  </label>
                </div>
              </div>

              <div className="form-field">
                <label className="form-label">Content (Markdown)</label>
                <div className="markdown-toolbar">
                  <button className="toolbar-btn" onClick={() => insertText("#### ", "")} title="Heading">H</button>
                  <button className="toolbar-btn" onClick={() => insertText("**", "**")} title="Bold">B</button>
                  <button className="toolbar-btn" onClick={() => insertText("*", "*")} title="Italic">I</button>
                  <button className="toolbar-btn" onClick={() => insertLinePrefix("* ")} title="Bullet list">• List</button>
                  <button className="toolbar-btn" onClick={() => insertLinePrefix("1. ")} title="Numbered list">1. List</button>
                  <button className="toolbar-btn" onClick={() => insertText("[", "](https://)")} title="Link">Link</button>
                  <button className="toolbar-btn" onClick={() => fileInputRef.current?.click()} title="Upload image">Image</button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                    style={{ display: "none" }}
                    onChange={handleImageUpload}
                  />
                  <button
                    className="toolbar-btn"
                    onClick={() => insertText("\n\n{{youtube:VIDEO_ID}}\n\n")}
                    title="YouTube embed"
                  >YouTube</button>
                </div>
                <textarea
                  id="markdown-editor"
                  className="form-textarea markdown-editor"
                  value={article.content_markdown || ""}
                  onChange={(e) => update("content_markdown", e.target.value)}
                  rows={20}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
